import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, AlertTriangle, Users, TrendingUp, Activity, Award,
  Calendar, Mail, Clock, BarChart3, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function formatDate(isoStr) {
  if (!isoStr) return 'Nunca';
  try {
    const d = new Date(isoStr);
    return d.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return isoStr; }
}

function diasDesde(isoStr) {
  if (!isoStr) return null;
  const diff = Date.now() - new Date(isoStr).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

export default function CampusMetricasPage() {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/campus/espacios/${spaceId}/metricas`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || 'Error cargando métricas'))
      .finally(() => setLoading(false));
  }, [spaceId]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-md w-full"><CardContent className="text-center py-8">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-gray-700">{error}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(`/campus/espacio/${spaceId}`)}>
          Volver al espacio
        </Button>
      </CardContent></Card>
    </div>
  );

  const { espacio, totales, estudiantes, en_riesgo, histograma_notas } = data;
  const maxHist = Math.max(...Object.values(histograma_notas), 1);

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4">
          <button onClick={() => navigate(`/campus/espacio/${spaceId}`)}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
            data-testid="volver-espacio-btn">
            <ArrowLeft className="w-4 h-4" /> Volver al espacio
          </button>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <BarChart3 className="w-8 h-8 text-violet-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Métricas</h1>
            <p className="text-sm text-gray-500">{espacio.nombre}</p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          <StatCard icon={Users} label="Estudiantes" value={totales.estudiantes} color="violet" />
          <StatCard icon={Activity} label="Activos (7d)" value={totales.activos_7d} color="emerald" />
          <StatCard icon={Clock} label="Inactivos (30d)" value={totales.inactivos_30d} color="amber" />
          <StatCard icon={TrendingUp} label="Completitud prom." value={`${totales.promedio_completitud_pct}%`} color="blue" />
          <StatCard icon={Award} label="Tareas / Quizzes" value={`${totales.tareas} / ${totales.quizzes}`} color="indigo" />
          <StatCard icon={AlertTriangle} label="En riesgo" value={totales.en_riesgo} color="red" />
        </div>

        {/* Histograma */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Distribución de notas (tareas + quizzes)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-32" data-testid="hist-notas">
              {Object.entries(histograma_notas).map(([bucket, count]) => (
                <div key={bucket} className="flex-1 flex flex-col items-center">
                  <div className="text-xs text-gray-500 mb-1">{count}</div>
                  <div className="w-full bg-violet-200 rounded-t transition-all"
                    style={{ height: `${(count / maxHist) * 100}%`, minHeight: count > 0 ? '4px' : '0' }} />
                  <div className="text-xs text-gray-400 mt-1">{bucket}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* En riesgo */}
        {en_riesgo.length > 0 && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2 text-amber-900">
                <AlertTriangle className="w-4 h-4" />
                Estudiantes en riesgo ({en_riesgo.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2" data-testid="lista-en-riesgo">
                {en_riesgo.map((e) => (
                  <li key={e.user_id} className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium text-gray-900">{e.nombre}</span>
                    {e.motivos.map((m) => (
                      <Badge key={m} variant="outline" className="bg-white text-amber-700 border-amber-300 text-xs">
                        {m}
                      </Badge>
                    ))}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Tabla de estudiantes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Detalle por estudiante</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="tabla-estudiantes">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Estudiante</th>
                    <th className="px-3 py-2 text-left font-medium">Tipo</th>
                    <th className="px-3 py-2 text-center font-medium">Última conexión</th>
                    <th className="px-3 py-2 text-center font-medium">Ingresos 7d</th>
                    <th className="px-3 py-2 text-center font-medium">Engagement</th>
                    <th className="px-3 py-2 text-center font-medium">Tareas</th>
                    <th className="px-3 py-2 text-center font-medium">Quizzes</th>
                    <th className="px-3 py-2 text-center font-medium">Completitud</th>
                    <th className="px-3 py-2 text-center font-medium">Promedio</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {estudiantes.map((e) => {
                    const dias = diasDesde(e.actividad.ultima_conexion);
                    const enRiesgo = e.riesgo && e.riesgo.length > 0;
                    return (
                      <tr key={e.user_id} className={enRiesgo ? 'bg-amber-50' : ''}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">{e.nombre}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {e.email}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="text-xs">
                            {e.kind === 'externo' ? 'Externo' : 'Matric.'}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-center text-xs">
                          {e.actividad.ultima_conexion ? (
                            <span className={dias > 14 ? 'text-red-600 font-medium' : 'text-gray-700'}>
                              {dias === 0 ? 'Hoy' : dias === 1 ? 'Ayer' : `Hace ${dias} días`}
                            </span>
                          ) : (
                            <span className="text-gray-400">Nunca</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">{e.actividad.ingresos_7d}</td>
                        <td className="px-3 py-2 text-center text-xs text-gray-600">
                          <div>{e.actividad.posts}p · {e.actividad.comentarios}c · {e.actividad.reacciones}r</div>
                          <div className="text-gray-400">{e.actividad.materiales_vistos} mat.</div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="text-gray-700">{e.performance.tareas_entregadas}/{e.performance.tareas_total}</div>
                          {e.performance.tareas_promedio !== null && (
                            <div className="text-xs text-gray-500">prom. {e.performance.tareas_promedio}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="text-gray-700">{e.performance.quizzes_hechos}/{e.performance.quizzes_total}</div>
                          {e.performance.quizzes_promedio !== null && (
                            <div className="text-xs text-gray-500">prom. {e.performance.quizzes_promedio}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                            <div
                              className={`h-full ${e.performance.completitud_pct < 50 ? 'bg-red-500' : e.performance.completitud_pct < 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                              style={{ width: `${e.performance.completitud_pct}%` }}
                            />
                          </div>
                          <div className="text-xs text-gray-600 mt-1">{e.performance.completitud_pct}%</div>
                        </td>
                        <td className="px-3 py-2 text-center font-medium">
                          {e.performance.promedio_general !== null ? (
                            <span className={e.performance.promedio_general < 6 ? 'text-red-600' : 'text-gray-900'}>
                              {e.performance.promedio_general}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {estudiantes.length === 0 && (
                    <tr><td colSpan={9} className="text-center text-gray-500 py-6">
                      No hay estudiantes con datos aún.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    violet: 'bg-violet-100 text-violet-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700',
    blue: 'bg-blue-100 text-blue-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    red: 'bg-red-100 text-red-700'
  };
  return (
    <div className="bg-white rounded-lg border p-3" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1.5 rounded ${colors[color]}`}><Icon className="w-3.5 h-3.5" /></div>
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <div className="text-xl font-bold text-gray-900">{value}</div>
    </div>
  );
}
